export function getFormFieldsPreferenceKey(formKey: string): string {
  return `form-fields.${formKey}`;
}

/** Persisted flag: all maskable fields minimized unless explicitly expanded. */
export const FORM_FIELDS_ALL_MINIMIZED_KEY = "__allMinimized";

/** Reserved key holding per-tab visibility (tabId -> visible). */
export const FORM_TABS_VISIBILITY_KEY = "__tabs";

/** Reserved keys that are not real field names. */
const RESERVED_KEYS = new Set<string>([
  FORM_FIELDS_ALL_MINIMIZED_KEY,
  FORM_TABS_VISIBILITY_KEY,
]);

/**
 * Per-user visibility grid for a form.
 * - Top-level string keys map a field name -> expanded (true) / minimized (false).
 * - The reserved `__tabs` key holds a nested map tabId -> visible.
 * - The reserved `__allMinimized` key minimizes every maskable field by default.
 *
 * The flat field-name shape is preserved for backward compatibility with
 * preferences persisted before tab-level visibility existed.
 */
export type FormFieldsVisibilityState = {
  [FORM_FIELDS_ALL_MINIMIZED_KEY]?: boolean;
  [FORM_TABS_VISIBILITY_KEY]?: Record<string, boolean>;
  [fieldName: string]: boolean | Record<string, boolean> | undefined;
};

export function isAllFieldsMinimized(
  state: FormFieldsVisibilityState,
): boolean {
  return state[FORM_FIELDS_ALL_MINIMIZED_KEY] === true;
}

/** true = field expanded (visible), false = minimized (label only). */
export function isFieldExpanded(
  state: FormFieldsVisibilityState,
  fieldName: string,
): boolean {
  if (RESERVED_KEYS.has(fieldName)) {
    return true;
  }
  const value = state[fieldName];
  if (isAllFieldsMinimized(state)) {
    return value === true;
  }
  if (typeof value === "boolean") return value !== false;
  return true;
}

// -- Tab-level visibility -----------------------------------------------------

/** Tabs are visible by default; only an explicit `false` hides them. */
export function isTabVisible(
  state: FormFieldsVisibilityState,
  tabId: string,
): boolean {
  const tabs = state[FORM_TABS_VISIBILITY_KEY];
  if (tabs && tabId in tabs) return tabs[tabId] !== false;
  return true;
}

/** Returns a new state with `tabId` visibility set. */
export function setTabVisibility(
  state: FormFieldsVisibilityState,
  tabId: string,
  visible: boolean,
): FormFieldsVisibilityState {
  const tabs = { ...(state[FORM_TABS_VISIBILITY_KEY] ?? {}) };
  tabs[tabId] = visible;
  return { ...state, [FORM_TABS_VISIBILITY_KEY]: tabs };
}

/** Ids of the tabs explicitly hidden by the user. */
export function getHiddenTabIds(state: FormFieldsVisibilityState): string[] {
  const tabs = state[FORM_TABS_VISIBILITY_KEY];
  if (!tabs) return [];
  return Object.keys(tabs).filter((id) => tabs[id] === false);
}

// -- Field label helpers ------------------------------------------------------

const FIELD_LOCALE_SUFFIX = /\.(fr|en|es|nl)$/;

/**
 * Splits a multilingual field name (e.g. `foo.fr`) into its base i18n key and
 * locale. Plain field names return `{ baseKey: name }`. Used to build readable
 * labels in the visibility drawers/menus (a raw `foo.fr` would not translate).
 */
export function splitFieldLocale(name: string): {
  baseKey: string;
  locale?: string;
} {
  const match = name.match(FIELD_LOCALE_SUFFIX);
  if (match && match.index != null) {
    return { baseKey: name.slice(0, match.index), locale: match[1] };
  }
  return { baseKey: name };
}

/** A field grouped by base key — multilingual variants (`x.fr`, `x.en`) merged. */
export type GroupedVisibilityField = {
  baseKey: string;
  label?: string;
  /** All registered field names for this base (per-locale variants or a single). */
  names: string[];
  /** Locales present, e.g. ["fr","en"]; empty for a non-multilingual field. */
  locales: string[];
  /** True only when every variant is non-maskable. */
  nonMaskable: boolean;
};

/**
 * Groups registered fields by their base key so a multilingual field (whose
 * variants register as `x.fr`, `x.en`, …) appears once instead of once per
 * language. Preserves first-seen order.
 */
export function groupFieldsByBaseLocale<
  T extends { name: string; label?: string; nonMaskable?: boolean },
>(fields: readonly T[]): GroupedVisibilityField[] {
  const order: string[] = [];
  const map = new Map<string, GroupedVisibilityField>();
  for (const field of fields) {
    const { baseKey, locale } = splitFieldLocale(field.name);
    let group = map.get(baseKey);
    if (!group) {
      group = { baseKey, label: field.label, names: [], locales: [], nonMaskable: true };
      map.set(baseKey, group);
      order.push(baseKey);
    }
    group.names.push(field.name);
    if (locale && !group.locales.includes(locale)) group.locales.push(locale);
    if (!group.label && field.label) group.label = field.label;
    // Group is non-maskable only if ALL its variants are.
    group.nonMaskable = group.nonMaskable && Boolean(field.nonMaskable);
  }
  return order.map((key) => map.get(key)!);
}
